import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import imageCompression from 'browser-image-compression';
import { Camera, X, Image, Upload, Loader2, ImagePlus } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 10; // Aumentato a 10 grazie alla compressione automatica

// Opzioni di compressione immagini
const COMPRESSION_OPTIONS = {
  maxSizeMB: 0.8,          // Massimo 800KB per immagine
  maxWidthOrHeight: 1920,  // Massimo 1920px (Full HD)
  useWebWorker: true,      // Usa Web Worker per non bloccare UI
  fileType: 'image/jpeg',  // Converti tutto in JPEG (più compatto)
  initialQuality: 0.7,     // Qualità iniziale 70%
};

/**
 * Comprime un'immagine per l'upload
 * Riduce drasticamente la dimensione mantenendo qualità accettabile
 */
const compressImage = async (file) => {
  // Se il file è già piccolo (< 500KB), non comprimere
  const fileSizeKB = file.size / 1024;
  if (fileSizeKB < 500) {
    console.log(`[Compression] ${file.name} già piccolo (${fileSizeKB.toFixed(0)}KB), skip compressione`);
    return file;
  }
  
  try {
    console.log(`[Compression] Inizio compressione ${file.name}: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
    
    const compressedFile = await imageCompression(file, COMPRESSION_OPTIONS);
    
    const originalSizeMB = file.size / 1024 / 1024;
    const compressedSizeMB = compressedFile.size / 1024 / 1024;
    const reduction = ((1 - compressedFile.size / file.size) * 100).toFixed(0);
    
    console.log(`[Compression] ${file.name}: ${originalSizeMB.toFixed(2)}MB → ${compressedSizeMB.toFixed(2)}MB (-${reduction}%)`);
    
    // Mantieni il nome originale ma con estensione .jpg
    const compressedFileName = file.name.replace(/\.[^/.]+$/, '.jpg');
    return new File([compressedFile], compressedFileName, { type: 'image/jpeg' });
  } catch (error) {
    console.error(`[Compression] Errore compressione ${file.name}:`, error);
    // In caso di errore, ritorna il file originale
    return file;
  }
};

/**
 * PhotoUpload Component
 * 
 * Modes:
 * 1. With reportId: Upload directly to server (for existing reports)
 * 2. Without reportId: Store locally and provide files for later upload (for new reports)
 * 
 * Props:
 * - reportId: ID of existing report (optional)
 * - photos: Array of existing photos from server
 * - onPhotosChange: Callback when photos change
 * - pendingPhotos: Array of pending File objects (for new reports)
 * - onPendingPhotosChange: Callback when pending photos change (for new reports)
 * - disabled: Disable all actions
 */
const PhotoUpload = ({ 
  reportId, 
  photos = [], 
  onPhotosChange, 
  pendingPhotos = [],
  onPendingPhotosChange,
  disabled = false 
}) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [previewUrls, setPreviewUrls] = useState([]);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Generate preview URLs for pending photos
  useEffect(() => {
    if (pendingPhotos.length === 0) {
      setPreviewUrls(prev => (prev.length === 0 ? prev : []));
      return;
    }
    const urls = pendingPhotos.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);
    
    // Cleanup URLs on unmount
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [pendingPhotos]);

  const totalPhotos = photos.length + pendingPhotos.length;

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check max photos limit
    if (totalPhotos + files.length > MAX_PHOTOS) {
      toast.error(`Massimo ${MAX_PHOTOS} foto per rapporto`);
      return;
    }

    // Validate and compress files
    const validFiles = [];
    toast.info('Elaborazione immagini in corso...');
    
    for (const file of files) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name}: deve essere un'immagine`);
        continue;
      }
      
      // Comprimi l'immagine
      try {
        const compressedFile = await compressImage(file);
        validFiles.push(compressedFile);
      } catch (err) {
        console.error(`Errore elaborazione ${file.name}:`, err);
        validFiles.push(file); // In caso di errore, usa l'originale
      }
    }

    if (validFiles.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    
    toast.success(`${validFiles.length} immagini pronte`);

    // If we have a reportId, upload directly with retry logic
    if (reportId) {
      setUploading(true);
      let successCount = 0;
      let failedCount = 0;
      const newPhotos = [...photos];

      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        let uploaded = false;
        
        // Retry logic: 3 tentativi per ogni foto
        for (let attempt = 1; attempt <= 3 && !uploaded; attempt++) {
          try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post(`${API}/reports/${reportId}/photos`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 120000 // 2 minuti timeout
            });

            if (response.data.success) {
              newPhotos.push(response.data.photo);
              successCount++;
              uploaded = true;
            }
          } catch (error) {
            console.warn(`[PhotoUpload] Tentativo ${attempt}/3 fallito per ${file.name}:`, error.message);
            if (attempt === 3) {
              toast.error(`Errore upload ${file.name}: ${error.response?.data?.detail || 'riprova più tardi'}`);
              failedCount++;
            } else {
              // Aspetta prima di riprovare (backoff esponenziale)
              await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            }
          }
        }
      }

      if (successCount > 0) {
        if (failedCount > 0) {
          toast.warning(`${successCount} foto caricate, ${failedCount} fallite`);
        } else {
          toast.success(`${successCount} foto caricate`);
        }
        onPhotosChange(newPhotos);
      } else if (failedCount > 0) {
        toast.error(`Nessuna foto caricata. Riprova.`);
      }
      setUploading(false);
    } else {
      // No reportId - store files locally for later upload
      const newPending = [...pendingPhotos, ...validFiles];
      onPendingPhotosChange(newPending);
      toast.success(`${validFiles.length} foto pronte per il caricamento`);
    }

    // Reset inputs
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
  };

  const handleDeleteExisting = async (photoId) => {
    if (!window.confirm('Eliminare questa foto?')) return;

    setDeleting(photoId);
    try {
      await axios.delete(`${API}/reports/${reportId}/photos/${photoId}`);
      const newPhotos = photos.filter(p => p.id !== photoId);
      onPhotosChange(newPhotos);
      toast.success('Foto eliminata');
    } catch (error) {
      toast.error('Errore eliminazione foto');
    }
    setDeleting(null);
  };

  const handleDeletePending = (index) => {
    const newPending = pendingPhotos.filter((_, i) => i !== index);
    onPendingPhotosChange(newPending);
  };

  const getPhotoUrl = (photo) => {
    // Se photo è una stringa (vecchio formato), usala direttamente
    if (typeof photo === 'string') {
      return `${API}/photos/${photo}`;
    }
    // Nuovo formato: oggetto con filename
    if (photo?.filename) {
      return `${API}/photos/${photo.filename}`;
    }
    // Fallback: se ha un id ma non filename, potrebbe essere in caricamento
    console.warn('Photo without filename:', photo);
    return null;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Camera className="w-4 h-4" />
          Foto Allegate
          <span className="text-xs bg-slate-200 px-2 py-0.5 rounded-full">
            {totalPhotos}/{MAX_PHOTOS}
          </span>
        </label>
        
        {!disabled && totalPhotos < MAX_PHOTOS && (
          <div className="flex gap-2">
            {/* Pulsante Galleria - senza capture, apre il selettore file */}
            <div className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={uploading}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-blue-500 text-blue-600 hover:bg-blue-50"
                disabled={uploading}
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Caricamento...</>
                ) : (
                  <><ImagePlus className="w-4 h-4 mr-1" />Galleria</>
                )}
              </Button>
            </div>
            
            {/* Pulsante Fotocamera - con capture, apre la fotocamera */}
            <div className="relative">
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={uploading}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-green-500 text-green-600 hover:bg-green-50"
                disabled={uploading}
              >
                <Camera className="w-4 h-4 mr-1" />Scatta
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Grid of photos */}
      {(photos.length > 0 || pendingPhotos.length > 0) && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {/* Existing photos from server */}
          {photos.map((photo, index) => {
            const photoUrl = getPhotoUrl(photo);
            return (
              <div 
                key={photo.id || index} 
                className="relative group aspect-square bg-slate-100 rounded-lg overflow-hidden border"
              >
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={`Foto ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100" height="100"/><text fill="%2394a3b8" x="50" y="55" text-anchor="middle" font-size="12">Errore</text></svg>';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-slate-200">
                    <Camera className="w-8 h-8 text-slate-400" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-white text-xs font-medium">Foto {index + 1}</span>
                </div>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleDeleteExisting(photo.id)}
                    disabled={deleting === photo.id}
                    className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                  >
                    {deleting === photo.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <X className="w-3 h-3" />
                    )}
                  </button>
                )}
              </div>
            );
          })}
          
          {/* Pending photos (local, not yet uploaded) */}
          {pendingPhotos.map((file, index) => (
            <div 
              key={`pending-${index}`} 
              className="relative group aspect-square bg-blue-50 rounded-lg overflow-hidden border-2 border-dashed border-blue-300"
            >
              <img
                src={previewUrls[index]}
                alt={`Nuova foto ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-blue-500/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="text-white text-xs font-medium">In attesa</span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-blue-500 text-white text-xs py-0.5 text-center">
                Da caricare
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleDeletePending(index)}
                  className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {photos.length === 0 && pendingPhotos.length === 0 && (
        <div className="flex items-center justify-center p-4 border-2 border-dashed border-slate-200 rounded-lg">
          <div className="text-center text-slate-400">
            <Image className="w-8 h-8 mx-auto mb-1" />
            <p className="text-xs">Nessuna foto allegata</p>
            {!disabled && (
              <p className="text-xs mt-1">Usa "Galleria" per scegliere foto esistenti o "Scatta" per usare la fotocamera</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Helper function to upload a single photo with retry logic
 */
const uploadSinglePhoto = async (reportId, file, maxRetries = 3) => {
  let lastError = null;
  const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/reports/${reportId}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000, // 60 secondi - ridotto grazie alla compressione
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });

      if (response.data.success) {
        return { success: true, photo: response.data.photo, filename: file.name };
      } else {
        lastError = new Error('Upload response non valida');
      }
    } catch (error) {
      lastError = error;
      const errorMsg = error.code === 'ECONNABORTED' ? 'Timeout - connessione lenta' : error.message;
      console.warn(`[PhotoUpload] Tentativo ${attempt}/${maxRetries} fallito per ${file.name} (${fileSizeMB}MB):`, errorMsg);
      
      // Se non è l'ultimo tentativo, aspetta prima di riprovare
      if (attempt < maxRetries) {
        const waitTime = 2000 * attempt; // Backoff più lungo: 2s, 4s, 6s
        console.log(`[PhotoUpload] Attesa ${waitTime/1000}s prima del prossimo tentativo...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }
  }
  
  return { 
    success: false, 
    filename: file.name, 
    error: lastError?.response?.data?.detail || lastError?.message || 'Errore sconosciuto' 
  };
};

/**
 * Helper function to upload pending photos after report creation
 * Call this after creating a report to upload all pending photos
 * 
 * Miglioramenti:
 * - COMPRESSIONE AUTOMATICA delle immagini prima dell'upload
 * - Upload con retry automatico (3 tentativi per foto)
 * - Timeout ridotto a 60s grazie alla compressione
 * - Report dettagliato di successi/fallimenti
 * - Callback opzionale per progress
 * - Pausa tra ogni foto per evitare sovraccarico
 */
export const uploadPendingPhotos = async (reportId, pendingPhotos, onProgress = null) => {
  if (!pendingPhotos || pendingPhotos.length === 0) return [];
  
  const uploadedPhotos = [];
  const failedPhotos = [];
  const total = pendingPhotos.length;
  
  console.log(`[PhotoUpload] Inizio upload di ${total} foto per rapporto ${reportId}`);
  
  // Upload sequenziale con retry per evitare sovraccarico del server
  for (let i = 0; i < pendingPhotos.length; i++) {
    const originalFile = pendingPhotos[i];
    const originalSizeMB = (originalFile.size / (1024 * 1024)).toFixed(2);
    
    // Notifica progresso - fase compressione
    if (onProgress) {
      onProgress({
        current: i + 1,
        total: total,
        filename: originalFile.name,
        status: 'compressing'
      });
    }
    
    // Comprimi l'immagine prima dell'upload
    let file = originalFile;
    try {
      console.log(`[PhotoUpload] Compressione ${i + 1}/${total}: ${originalFile.name} (${originalSizeMB}MB)`);
      file = await compressImage(originalFile);
      const compressedSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      console.log(`[PhotoUpload] Compresso: ${originalSizeMB}MB → ${compressedSizeMB}MB`);
    } catch (compressionError) {
      console.warn(`[PhotoUpload] Compressione fallita, uso originale:`, compressionError);
    }
    
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    
    // Notifica progresso - fase upload
    if (onProgress) {
      onProgress({
        current: i + 1,
        total: total,
        filename: originalFile.name,
        status: 'uploading'
      });
    }
    
    console.log(`[PhotoUpload] Caricamento ${i + 1}/${total}: ${file.name} (${fileSizeMB}MB)`);
    
    const result = await uploadSinglePhoto(reportId, file);
    
    if (result.success) {
      uploadedPhotos.push(result.photo);
      console.log(`[PhotoUpload] ✅ Successo ${i + 1}/${total}: ${file.name}`);
    } else {
      failedPhotos.push({ filename: originalFile.name, error: result.error });
      console.error(`[PhotoUpload] ❌ Fallito ${i + 1}/${total}: ${originalFile.name} - ${result.error}`);
      // Mostra toast di errore per ogni foto fallita
      toast.error(`Foto "${originalFile.name}" non caricata: ${result.error}`);
    }
    
    // Pausa di 300ms tra ogni upload (ridotta grazie alla compressione)
    if (i < pendingPhotos.length - 1) {
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  }
  
  // Log riassuntivo
  console.log(`[PhotoUpload] Completato: ${uploadedPhotos.length}/${total} foto caricate con successo`);
  if (failedPhotos.length > 0) {
    console.error(`[PhotoUpload] ${failedPhotos.length} foto non caricate:`, failedPhotos);
  }
  
  // Notifica completamento
  if (onProgress) {
    onProgress({
      current: total,
      total: total,
      status: 'completed',
      uploaded: uploadedPhotos.length,
      failed: failedPhotos.length,
      failedFiles: failedPhotos
    });
  }
  
  return uploadedPhotos;
};

export default PhotoUpload;
